/**
 * enrichableSelection — WHICH LISTING ROWS CAN ACTUALLY BE ENRICHED (task-5010-split, #5012).
 *
 * ⛔⛔ THE DEFECT THIS EXISTS TO END. GET /offering and /offering/ids return `id` from more than one
 * table: a knowledge_base_entries id when the product has a KB row, a product_feed_items id when it
 * does not. POST /product-enrichment/batch-enrich-async takes KBE ids and is ALL-OR-NOTHING
 * (product-enrichment.js:4392), so ONE feed id refuses the WHOLE batch.
 * Measured on db2 2026-08-10: Motocard 8,201 feed items, 2,058 with a KB row → 6,143 WITHOUT (75%).
 * ⇒ Select-all on Motocard therefore built a batch that MUST be refused, and the 2,058 enrichable
 *   products were refused along with the rest. The operator could enrich NOTHING from that screen.
 *
 * 🔑 THE RULE, AND IT IS THE OPPOSITE OF THE TEMPTING ONE: EXCLUDING IS NOT ENOUGH — THE EXCLUSION
 * HAS TO BE REPORTED. Quietly dropping 6,143 rows and enriching 2,058 turns a loud, correctly
 * diagnosed 400 into a silent narrowing: the operator asks for the catalogue, is told "2,058 queued",
 * and never learns the other three quarters were dropped or why. That trades a visible failure for an
 * invisible one, which is strictly worse. So the caller gets BOTH lists and is expected to say so.
 */

/** One row as the listing returns it. `id_kind` names which table `id` came from. */
export interface OfferingIdItem {
  id: string;
  id_kind?: string | null;
}

export interface EnrichablePartition {
  /** Ids the enrichment endpoint can actually consume. */
  enrichable: string[];
  /** Rows it cannot, kept WITH their kind so the caller can explain rather than just subtract. */
  excluded: OfferingIdItem[];
  /** Every kind seen, for the sentence shown to the operator. */
  countsByKind: Record<string, number>;
}

/**
 * Split listing rows into what the endpoint can consume and what it cannot.
 *
 * ⛔ THROWS on a row with no `id_kind` rather than assuming one. An unlabelled row is exactly the
 * state this whole change removes; guessing 'kbe' here would reinstate the original defect behind a
 * helper that looks like it prevents it. A backend that stops sending the field must break loudly.
 */
export function partitionByEnrichability(
  items: OfferingIdItem[],
  endpointIdKind: string = 'kbe'
): EnrichablePartition {
  if (!Array.isArray(items)) {
    throw new Error(
      'Product id list was not an array — the listing response shape changed. Refusing to guess '
      + 'which products were selected.'
    );
  }

  const enrichable: string[] = [];
  const excluded: OfferingIdItem[] = [];
  const countsByKind: Record<string, number> = {};

  for (const item of items) {
    if (!item || typeof item.id !== 'string' || !item.id) {
      throw new Error('Product id list contained an entry with no id — refusing to build a selection from it.');
    }
    const kind = item.id_kind;
    if (typeof kind !== 'string' || !kind) {
      throw new Error(
        `Product ${item.id} arrived with no id_kind, so there is no way to tell which table it came `
        + 'from. This is the exact ambiguity that made enrichment fail silently; refusing to guess.'
      );
    }
    countsByKind[kind] = (countsByKind[kind] || 0) + 1;
    if (kind === endpointIdKind) enrichable.push(item.id);
    else excluded.push(item);
  }

  return { enrichable, excluded, countsByKind };
}

/**
 * The sentence shown to the operator when rows were left out.
 *
 * ⚠️ NAMES THE CAUSE, NOT JUST THE COUNT. "6,143 excluded" invites the reading that they are broken
 * or already done. They are neither: they have no knowledge-base entry yet, which is a FEED INGESTION
 * state, and the operator's next action is different for that than for a failure.
 */
export function describeExcluded(partition: EnrichablePartition): string | null {
  if (partition.excluded.length === 0) return null;
  const feed = partition.countsByKind.feed || 0;
  const other = partition.excluded.length - feed;
  const parts: string[] = [];
  if (feed > 0) {
    parts.push(
      `${feed.toLocaleString()} product(s) are not in the knowledge base yet, so they cannot be `
      + 'enriched until their feed has been ingested'
    );
  }
  if (other > 0) {
    parts.push(`${other.toLocaleString()} product(s) came from a source this action cannot target`);
  }
  return `${parts.join('; ')}. They were NOT included and nothing was run for them.`;
}
